import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Drawer } from '@/ui/Drawer';
import { Field, Input, InputSuelto, SelectSuelto } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { SelectorDeIcono } from '@/components/SelectorDeIcono';
import {
  TIPOS_BASICOS, TIPOS_INTELIGENTES, clavePara, esInteligente,
  useBorrarRecurso, useCatalogos, useGuardarRecurso, useRecurso, useRecursos,
  type Campo, type DatosSensibles, type ReglaDeIndicador, type TipoDeCampo,
} from './api';
import { ConfigDeCampoInteligente } from './camposInteligentes';
import { BLOQUEADAS, claveDe, columnasSinDecidir, laLlenaElColaborador, sinDecidir } from './reglas';

/**
 * FormularioDeRecurso — el constructor de columnas. Es la pieza más densa del
 * módulo 15 y la que decide qué se le pide al colaborador.
 *
 * ## Lo que cambia
 *
 * - **Es un panel, no un modal de 620 px.** Dentro hay diecisiete tipos de
 *   columna con su configuración: en el móvil aquello no cabía.
 * - **Eliminar el recurso ya no cierra el formulario para preguntar.** El
 *   vanilla llamaba a `Utils.confirm`, que reutiliza el mismo `#modal-overlay`
 *   que estaba abierto.
 * - **El selector de «quién la llena» conserva sus tres estados** —sin decidir,
 *   el colaborador, solo Gestión Humana— porque el servidor los exige
 *   (`columnas_sin_decidir`). Lo que cambia es que ahora también se ve en el
 *   resumen de arriba antes de guardar, y no solo al recibir el 400.
 */
export function FormularioDeRecurso({ id, onCerrar }: { id: number | null; onCerrar: () => void }) {
  const consulta = useRecurso(id);
  const todos = useRecursos();
  const catalogos = useCatalogos(true);
  const guardar = useGuardarRecurso();
  const borrar = useBorrarRecurso();

  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [icono, setIcono] = useState('bi-database');
  const [color, setColor] = useState('#0D2B4E');
  const [campos, setCampos] = useState<Campo[]>([
    { key: '', label: 'Nombre', type: 'text', required: true, options: [], config: {} },
  ]);
  const [error, setError] = useState('');
  const [borrando, setBorrando] = useState(false);

  const r = consulta.data;
  const esSistema = !!r?.is_system;
  const protegidas: string[] = esSistema ? (r?.protected_keys ?? []) : [];
  const sensiblesDelMaestro: DatosSensibles | null = r?.datos_sensibles ?? null;

  // Los valores llegan cuando llega el recurso, y una sola vez: a partir de ahí
  // el formulario es de quien escribe.
  useEffect(() => {
    if (!r) return;
    setNombre(r.name);
    setDescripcion(r.description ?? '');
    setIcono(r.icon || 'bi-database');
    setColor(r.color || '#0D2B4E');
    setCampos((r.fields ?? []).map((f) => ({ ...f, config: f.config ?? {} })));
  }, [r]);

  const pendientes = useMemo(
    () => columnasSinDecidir(campos, esSistema), [campos, esSistema]);

  function cambiar(i: number, parche: Partial<Campo>) {
    setCampos((cs) => cs.map((c, j) => (j === i ? { ...c, ...parche } : c)));
    setError('');
  }

  function cambiarConfig(i: number, clave: string, valor: string | number | ReglaDeIndicador[] | undefined) {
    setCampos((cs) => cs.map((c, j) => (j === i ? { ...c, config: { ...c.config, [clave]: valor } } : c)));
  }

  function enviar() {
    const limpios = campos
      .filter((c) => (c.label || '').trim())
      // La clave se fija al guardar para que las referencias de los campos
      // inteligentes (source, this_field…) sigan apuntando a algo.
      .map((c) => ({ ...c, key: c.key || clavePara(c.label) }));
    if (!nombre.trim()) { setError('Escribe el nombre del recurso.'); return; }
    if (!limpios.length) { setError('Agrega al menos una columna con etiqueta.'); return; }
    if (pendientes.length) {
      setError(`Falta decir quién llena: ${pendientes.map((c) => c.label).join(', ')}.`);
      return;
    }
    guardar.mutate(
      {
        id,
        cuerpo: {
          name: nombre.trim(),
          description: descripcion.trim() || null,
          icon: icono || 'bi-database',
          color,
          fields: limpios,
        },
      },
      {
        onSuccess: (recurso) => {
          Utils.toast(id ? 'Recurso actualizado' : 'Recurso creado', 'success');
          onCerrar();
          // Un recurso recién creado se abre: sin columnas no sirve de nada, y
          // es lo que hacía el vanilla.
          if (!id && recurso?.id) location.hash = `#recursos-privados/recurso/${recurso.id}`;
        },
        onError: (err) => setError(err instanceof Error ? err.message : 'No se pudo guardar'),
      },
    );
  }

  return (
    <Drawer
      abierto
      onCambio={(v) => { if (!v && !guardar.isPending) onCerrar(); }}
      titulo={id ? 'Columnas del recurso' : 'Nuevo recurso'}
      descripcion="Cada columna define qué se guarda en cada registro. Los tipos inteligentes se calculan solos."
      ancho="lg"
      pie={
        <>
          {id != null && !esSistema && (
            <Button
              variant="ghost"
              className="mr-auto text-danger-ink"
              onClick={() => setBorrando(true)}
            >
              <i className="bi bi-trash" aria-hidden="true" /> Eliminar recurso
            </Button>
          )}
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={enviar} loading={guardar.isPending}>
            <i className="bi bi-check-lg" aria-hidden="true" /> Guardar recurso
          </Button>
        </>
      }
    >
      {id != null && consulta.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : id != null && consulta.isError ? (
        <EstadoError err={consulta.error} que="el recurso" onReintentar={() => void consulta.refetch()} />
      ) : (
        <div className="flex flex-col gap-4">
          {protegidas.length > 0 && (
            <p className="flex items-start gap-2 rounded-sm border border-info/40 bg-info/12 p-3 text-2 text-ink-2">
              <i className="bi bi-shield-lock mt-0.5 shrink-0 text-info-ink" aria-hidden="true" />
              <span>
                Base de Datos Empleados: puedes <strong>agregar y editar</strong> columnas, pero
                las columnas base no se eliminan.
              </span>
            </p>
          )}

          <Field label="Nombre del recurso" error={error || undefined}>
            <Input
              value={nombre}
              disabled={esSistema}
              onChange={(e) => { setNombre(e.target.value); setError(''); }}
              placeholder="Ej: Proveedores"
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <Field label="Ícono">
              <SelectorDeIcono id="res-icono" valor={icono} onCambio={setIcono} respaldo="bi-database" />
            </Field>
            <Field label="Color">
              <input
                type="color"
                aria-label="Color de la portada"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-11 w-16 cursor-pointer rounded-sm border border-field-border bg-surface p-1"
              />
            </Field>
          </div>

          <Field label="Descripción" opcional>
            <Input value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
          </Field>

          {esSistema && (
            <ResumenDeAutogestion
              campos={campos}
              pendientes={pendientes}
              datosSensibles={sensiblesDelMaestro}
            />
          )}

          <section>
            <h3 className="mb-2 text-3 font-bold text-brand-ink">
              Columnas <span className="font-normal text-muted">({campos.length})</span>
            </h3>
            <ul className="flex flex-col gap-2">
              {campos.map((campo, i) => (
                <li key={i}>
                  <FilaDeColumna
                    campo={campo}
                    indice={i}
                    otros={campos.filter((_, j) => j !== i).filter((c) => claveDe(c))}
                    recursos={(todos.data ?? []).filter((x) => x.id !== id)}
                    catalogos={catalogos.data ?? []}
                    esSistema={esSistema}
                    protegida={protegidas.includes(campo.key)}
                    onCambio={(parche) => cambiar(i, parche)}
                    onConfig={(clave, valor) => cambiarConfig(i, clave, valor)}
                    onQuitar={() => setCampos((cs) => cs.filter((_, j) => j !== i))}
                  />
                </li>
              ))}
            </ul>
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              onClick={() => setCampos((cs) => [
                ...cs, { key: '', label: '', type: 'text', required: false, options: [], config: {} },
              ])}
            >
              <i className="bi bi-plus-lg" aria-hidden="true" /> Agregar columna
            </Button>
          </section>
        </div>
      )}

      {id != null && (
        <ConfirmDialog
          abierto={borrando}
          onCambio={setBorrando}
          titulo="Eliminar el recurso"
          descripcion={`Se eliminan «${r?.name ?? ''}» y sus ${r?.entry_count ?? 0} registros. Queda constancia en la auditoría y no se puede deshacer desde aquí.`}
          textoConfirmar="Eliminar recurso"
          destructivo
          cargando={borrar.isPending}
          onConfirmar={() => borrar.mutate(id, {
            onSuccess: () => {
              Utils.toast('Recurso eliminado', 'success');
              setBorrando(false);
              onCerrar();
            },
            onError: (err) => {
              setBorrando(false);
              Utils.toast(err instanceof Error ? err.message : 'No se pudo eliminar', 'error');
            },
          })}
        />
      )}
    </Drawer>
  );
}

/* ── Una columna ───────────────────────────────────────────────────────── */

function FilaDeColumna({
  campo, indice, otros, recursos, catalogos, esSistema, protegida,
  onCambio, onConfig, onQuitar,
}: {
  campo: Campo;
  indice: number;
  otros: Campo[];
  recursos: { id: number; name: string }[];
  catalogos: { key: string; label: string }[];
  esSistema: boolean;
  protegida: boolean;
  onCambio: (parche: Partial<Campo>) => void;
  onConfig: (clave: string, valor: string | number | ReglaDeIndicador[] | undefined) => void;
  onQuitar: () => void;
}) {
  const inteligente = esInteligente(campo.type);
  const n = indice + 1;

  return (
    <div
      className={cn(
        'rounded-sm border border-line p-2',
        // El tinte marca las que se calculan solas. Es token, no `rgba()` a mano.
        inteligente ? 'bg-accent/8' : 'bg-surface',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <InputSuelto
          aria-label={`Etiqueta de la columna ${n}`}
          className="min-w-[9rem] flex-1 py-1 text-2"
          value={campo.label ?? ''}
          onChange={(e) => onCambio({ label: e.target.value })}
          placeholder="Etiqueta"
        />
        <SelectSuelto
          aria-label={`Tipo de la columna ${n}`}
          className="w-auto py-1 text-2"
          value={campo.type}
          onChange={(e) => onCambio({ type: e.target.value as TipoDeCampo })}
        >
          <optgroup label="Básicos">
            {TIPOS_BASICOS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </optgroup>
          <optgroup label="Inteligentes">
            {TIPOS_INTELIGENTES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </optgroup>
        </SelectSuelto>

        {campo.type === 'select' && (
          <SelectSuelto
            aria-label={`Origen de las opciones de la columna ${n}`}
            className="w-auto py-1 text-2"
            value={campo.catalog_source ?? ''}
            onChange={(e) => onCambio({ catalog_source: e.target.value || undefined })}
          >
            <option value="">— Opciones a mano —</option>
            {catalogos.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </SelectSuelto>
        )}
        {campo.type === 'select' && !campo.catalog_source && (
          <InputSuelto
            aria-label={`Opciones de la columna ${n}`}
            className="w-36 py-1 text-2"
            value={(campo.options ?? []).join(', ')}
            onChange={(e) => onCambio({
              options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
            })}
            placeholder="opc1, opc2"
          />
        )}
        {campo.type === 'select' && campo.catalog_source && (
          <span className="text-1 text-muted">
            <i className="bi bi-link-45deg" aria-hidden="true" /> opciones del catálogo
          </span>
        )}

        {!inteligente && (
          <label className="flex items-center gap-1 whitespace-nowrap text-1 text-ink-2">
            <input
              type="checkbox"
              checked={!!campo.required}
              onChange={(e) => onCambio({ required: e.target.checked })}
            />
            obligatoria
          </label>
        )}

        <QuienLaLlena campo={campo} indice={indice} esSistema={esSistema} onCambio={onCambio} />

        {protegida ? (
          <span
            className="inline-flex items-center px-1 text-muted"
            title="Columna base: no se puede eliminar"
          >
            <i className="bi bi-lock" aria-hidden="true" />
            <span className="sr-only">Columna base, no se puede eliminar</span>
          </span>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={`Quitar la columna ${campo.label || n}`}
            onClick={onQuitar}
          >
            <i className="bi bi-x-lg text-danger-ink" aria-hidden="true" />
          </Button>
        )}
      </div>

      {inteligente && (
        <div className="pt-2">
          <ConfigDeCampoInteligente
            campo={campo}
            otros={otros}
            recursos={recursos}
            onCambio={onConfig}
          />
        </div>
      )}
    </div>
  );
}

/** El selector de tres estados, y la casilla de dato sensible. Solo en la Base
 *  de Datos Empleados: es el único recurso que alimenta el formulario de datos
 *  iniciales del colaborador. */
function QuienLaLlena({
  campo, indice, esSistema, onCambio,
}: {
  campo: Campo;
  indice: number;
  esSistema: boolean;
  onCambio: (parche: Partial<Campo>) => void;
}) {
  if (!esSistema || esInteligente(campo.type) || campo.readonly) return null;

  if (BLOQUEADAS.includes(campo.key)) {
    return (
      <span
        className="inline-flex items-center gap-1 whitespace-nowrap text-1 text-muted"
        title="Este dato define quién aprueba las solicitudes del colaborador; solo lo administra Gestión Humana"
      >
        <i className="bi bi-shield-lock" aria-hidden="true" /> solo GH
      </span>
    );
  }

  const pendiente = sinDecidir(campo);
  const valor = pendiente ? '' : String(laLlenaElColaborador(campo));

  return (
    <>
      <SelectSuelto
        aria-label={`Quién llena la columna ${campo.label || indice + 1}`}
        className={cn('w-auto py-1 text-1', pendiente && 'border-warn-ink text-warn-ink')}
        value={valor}
        onChange={(e) => onCambio(
          e.target.value === '' ? { autogestion: undefined } : { autogestion: e.target.value === 'true' },
        )}
      >
        {pendiente && <option value="">— ¿quién la llena? —</option>}
        <option value="true">La llena el colaborador</option>
        <option value="false">Solo Gestión Humana</option>
      </SelectSuelto>
      <label
        className="flex items-center gap-1 whitespace-nowrap text-1 text-ink-2"
        title="Salud, EPS, discapacidad, datos de hijos, afiliación sindical o política, biometría. Marcarlo obliga a pedir la autorización de tratamiento de datos sensibles antes de recogerlo (Ley 1581, art. 5 y 6)."
      >
        <input
          type="checkbox"
          checked={!!campo.sensible}
          onChange={(e) => onCambio({ sensible: e.target.checked })}
        />
        <i className="bi bi-shield-exclamation" aria-hidden="true" /> sensible
      </label>
    </>
  );
}

/* ── El resumen de arriba ──────────────────────────────────────────────── */

/**
 * Qué se le está pidiendo al colaborador, antes de abrir columna por columna.
 * Sin esto, una columna que no debía ser suya —salario, observaciones
 * internas— pasa desapercibida.
 */
function ResumenDeAutogestion({
  campos, pendientes, datosSensibles,
}: {
  campos: Campo[];
  pendientes: Campo[];
  datosSensibles: DatosSensibles | null;
}) {
  const suyas = campos.filter((c) => !sinDecidir(c) && laLlenaElColaborador(c));
  const sensibles = campos.filter((c) => c.sensible);

  return (
    <section className="flex flex-col gap-2 rounded-sm border border-line bg-sunken p-3 text-2">
      {pendientes.length > 0 && (
        <p className="flex items-start gap-2 text-warn-ink">
          <i className="bi bi-exclamation-triangle-fill mt-0.5 shrink-0" aria-hidden="true" />
          <span>
            Falta decir quién llena <strong>{pendientes.length}</strong>{' '}
            {pendientes.length === 1 ? 'columna' : 'columnas'}:{' '}
            {pendientes.map((c) => c.label || c.key || 'sin nombre').join(', ')}
          </span>
        </p>
      )}
      <p className="flex items-start gap-2 text-ink-2">
        <i className="bi bi-person-check mt-0.5 shrink-0" aria-hidden="true" />
        <span>
          El colaborador llena <strong>{suyas.length}</strong> de {campos.length} columnas
          {suyas.length ? `: ${suyas.map((c) => c.label || c.key).join(', ')}` : ''}
        </span>
      </p>
      {sensibles.length > 0 && (
        <AvisoDeSensibles cuantas={sensibles.length} datos={datosSensibles} />
      )}
      <p className="flex items-start gap-2 text-muted">
        <i className="bi bi-info-circle mt-0.5 shrink-0" aria-hidden="true" />
        <span>
          Cada columna que agregues necesita decir quién la diligencia. Lo interno —salario,
          observaciones de Gestión Humana, motivo de retiro— va como{' '}
          <strong>Solo Gestión Humana</strong>.
        </span>
      </p>
    </section>
  );
}

/**
 * El alcance real de una columna sensible.
 *
 * Marcarla no basta: `autorizo_datos_sensibles()` es cerrada por defecto, así
 * que el campo solo le aparece a quien haya aceptado la autorización — que es
 * voluntaria (Ley 1581 art. 6) y no se puede exigir. El número no es un error a
 * corregir: es a cuánta gente se le está pidiendo de verdad.
 */
function AvisoDeSensibles({ cuantas, datos }: { cuantas: number; datos: DatosSensibles | null }) {
  const cab = <><strong>{cuantas}</strong> {cuantas === 1 ? 'marcada' : 'marcadas'} como dato sensible</>;

  if (!datos) {
    return (
      <p className="flex items-start gap-2 text-ink-2">
        <i className="bi bi-shield-exclamation mt-0.5 shrink-0" aria-hidden="true" />
        <span>{cab} — se piden solo con autorización</span>
      </p>
    );
  }

  if (!datos.documento) {
    return (
      <p className="flex items-start gap-2 text-danger-ink">
        <i className="bi bi-exclamation-triangle-fill mt-0.5 shrink-0" aria-hidden="true" />
        <span>
          {cab}, y <strong>no hay autorización de datos sensibles publicada</strong>: no se le
          puede pedir a nadie. Publícala en Administración → Legal.
        </span>
      </p>
    );
  }

  const faltan = Math.max(0, (datos.colaboradores || 0) - (datos.autorizados || 0));
  const cuerpo = (
    <>
      {cab} — hoy {cuantas === 1 ? 'la ve' : 'las ven'} <strong>{datos.autorizados}</strong> de{' '}
      {datos.colaboradores} colaboradores, los que han autorizado el tratamiento.
    </>
  );

  return faltan ? (
    <p className="flex items-start gap-2 text-warn-ink">
      <i className="bi bi-exclamation-triangle-fill mt-0.5 shrink-0" aria-hidden="true" />
      <span>
        {cuerpo} A {faltan === 1 ? 'el otro' : `los otros ${faltan}`} no{' '}
        {faltan === 1 ? 'le' : 'les'} aparece en Mi ficha. La autorización es voluntaria: no se
        puede exigir, solo volver a ofrecerla.
      </span>
    </p>
  ) : (
    <p className="flex items-start gap-2 text-ink-2">
      <i className="bi bi-shield-exclamation mt-0.5 shrink-0" aria-hidden="true" />
      <span>{cuerpo}</span>
    </p>
  );
}
